import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import ReactApexChart from "react-apexcharts";
import {
  MdOutlineReceiptLong,
  MdOutlinePayments,
  MdOutlineAccountBalanceWallet,
  MdOutlineEditNote,
  MdOutlineSearch,
  MdOutlineFileDownload,
  MdAdd,
  MdOutlineBusinessCenter,
} from "react-icons/md";
import axios from "../../config/axiosInstance";
import { PAYMENTS } from "../../config/apiConfig";
import {
  Badge,
  Card,
  EmptyState,
  ErrorBlock,
  SkeletonRows,
} from "../crm/Primitives";
import {
  SkeletonHeader,
  SkeletonKpiStrip,
  SkeletonChart,
  SkeletonTable,
} from "../crm/Skeletons";
import {
  SERIES,
  TEXTO_EIXO,
  baseChartOptions,
  barPlotOptions,
  barDataLabels,
} from "../crm/chartTheme";
import { formatDate, formatMoney, formatNumber, formatPercent } from "../crm/crmHelpers";
import Modal from "../modals/Modal";
import ClientPicker from "../crm/ClientPicker";
import { CLIENTS } from "../../config/apiConfig";

/*
  Pagamentos recebidos pela Relinq.

  A versão anterior eram quatro cartões com fundos coloridos arbitrários (um
  azul, um verde, um roxo — a cor não dizia nada), um bloco de filtros chamado
  "Filtros inteligentes" para três campos comuns, e uma tabela sem nenhum
  gráfico. Pior: a tela mostrava "nenhum pagamento encontrado" com 119
  registros no banco, porque o backend descartava todo pagamento cujo provedor
  não fosse identificável — ver paymentService.js.

  Agora: indicadores consistentes com o resto do painel, a curva mensal (que é
  a pergunta real de uma tela de pagamentos — "estamos recebendo mais ou
  menos?") e os filtros numa linha só.
*/

const PROVEDORES = [
  { value: "", label: "Todas as origens" },
  { value: "asaas", label: "Asaas" },
  { value: "mercado", label: "Mercado Pago" },
  { value: "manual", label: "Lançamento manual" },
  { value: "outro", label: "Outro / não identificado" },
];

const PROVEDOR_META = {
  asaas: { label: "Asaas", tone: "success" },
  mercado_pago: { label: "Mercado Pago", tone: "info" },
  manual: { label: "Manual", tone: "warning" },
  unknown: { label: "Outro", tone: "neutral" },
};

const Kpi = ({ icon: Icon, label, value, hint, tone = "neutral" }) => (
  <div className="flex flex-1 items-start gap-3 border-slate-200 px-5 py-4 sm:border-r sm:last:border-r-0">
    <span
      className={`shrink-0 rounded-xl p-2 ${
        tone === "success"
          ? "bg-emerald-50 text-emerald-600"
          : tone === "warning"
          ? "bg-amber-50 text-amber-600"
          : tone === "info"
          ? "bg-sky-50 text-sky-600"
          : "bg-slate-100 text-slate-500"
      }`}
    >
      <Icon size={18} />
    </span>
    <span className="min-w-0">
      <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
        {label}
      </span>
      <span className="block text-xl font-bold leading-tight text-slate-900">
        {value}
      </span>
      {hint ? (
        <span className="block text-[11px] text-slate-500">{hint}</span>
      ) : null}
    </span>
  </div>
);

const ReceitaChart = ({ series }) => {
  if (!series?.length) return null;

  const categorias = series.map((item) => item.label);

  const options = {
    ...baseChartOptions(categorias, {
      yaxis: {
        labels: {
          style: { colors: TEXTO_EIXO, fontSize: "11px" },
          formatter: (value) =>
            value >= 1000 ? `${Math.round(value / 1000)}k` : Math.round(value),
        },
      },
    }),
    plotOptions: barPlotOptions(),
    dataLabels: barDataLabels((value) =>
      value >= 1000 ? `${(value / 1000).toFixed(1)}k` : Math.round(value)
    ),
    colors: [SERIES.dinheiro],
    tooltip: {
      shared: false,
      intersect: true,
      style: { fontSize: "12px", fontFamily: "inherit" },
      y: {
        formatter: (value, { dataPointIndex }) =>
          `${formatMoney(value)} · ${formatNumber(
            series[dataPointIndex].count
          )} pagamento(s)`,
      },
    },
  };

  return (
    <ReactApexChart
      type="bar"
      height={200}
      options={options}
      series={[{ name: "Recebido", data: series.map((item) => item.amount) }]}
    />
  );
};

const EXTENSOES = [
  { value: "none", label: "Não mexer no vencimento" },
  { value: "30", label: "+ 30 dias" },
  { value: "1m", label: "+ 1 mês" },
  { value: "3m", label: "+ 3 meses" },
  { value: "6m", label: "+ 6 meses" },
  { value: "12m", label: "+ 12 meses" },
];

const FORMAS = [
  { value: "PIX", label: "PIX" },
  { value: "TRANSFER", label: "Transferência" },
  { value: "CASH", label: "Dinheiro" },
  { value: "CARD", label: "Cartão (fora do sistema)" },
  { value: "BOLETO", label: "Boleto" },
  { value: "OTHER", label: "Outro" },
];

/*
  Data de hoje montada por componentes locais. `toISOString()` devolveria o dia
  em UTC e, de madrugada, o campo abriria com a data de ontem.
*/
const hojeIso = () => {
  const agora = new Date();
  return `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, "0")}-${String(
    agora.getDate()
  ).padStart(2, "0")}`;
};

const campoClass =
  "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100";

const PaymentsTable = () => {
  const [filtros, setFiltros] = useState({
    startDate: "",
    endDate: "",
    provider: "",
  });
  const [aplicados, setAplicados] = useState(filtros);
  const [pagina, setPagina] = useState(1);
  const [criando, setCriando] = useState(false);
  const [cliente, setCliente] = useState(null);
  const [erroForm, setErroForm] = useState(null);
  const [form, setForm] = useState({
    value: "",
    paidAt: hojeIso(),
    billingType: "PIX",
    extend: "none",
    note: "",
  });

  const queryClient = useQueryClient();

  const registrar = useMutation(
    () => axios.post(`${CLIENTS.ONE}/${cliente.id}/payments`, form),
    {
      onSuccess: () => {
        setErroForm(null);
        setCriando(false);
        setCliente(null);
        setForm({
          value: "",
          paidAt: hojeIso(),
          billingType: "PIX",
          extend: "none",
          note: "",
        });
        queryClient.invalidateQueries(["payments"]);
        queryClient.invalidateQueries(["crm"]);
      },
      onError: (error) =>
        setErroForm(
          error.response?.data?.message ||
            "Não foi possível registrar o pagamento."
        ),
    }
  );

  const { data, isLoading, error, refetch, isFetching } = useQuery(
    ["payments", aplicados, pagina],
    async () => {
      const params = new URLSearchParams({ page: String(pagina) });
      if (aplicados.startDate) params.set("startDate", aplicados.startDate);
      if (aplicados.endDate) params.set("endDate", aplicados.endDate);
      if (aplicados.provider) params.set("provider", aplicados.provider);

      const { data: resposta } = await axios.get(
        `${PAYMENTS.HISTORY}?${params.toString()}`
      );
      return resposta.data;
    },
    { keepPreviousData: true }
  );

  const aplicar = () => {
    setPagina(1);
    setAplicados(filtros);
  };

  const limpar = () => {
    const vazio = { startDate: "", endDate: "", provider: "" };
    setFiltros(vazio);
    setAplicados(vazio);
    setPagina(1);
  };

  const resumo = data?.summary;
  const ticket =
    resumo?.approvedCount > 0 ? resumo.totalAmount / resumo.approvedCount : 0;

  /*
    Exportação em CSV montada no navegador, a partir da página carregada.
    Não é o recorte inteiro — e o rótulo diz isso, porque um arquivo que se
    apresenta como "todos os pagamentos" e traz 20 linhas é pior do que não
    existir.
  */
  const exportarPagina = () => {
    const linhas = [
      ["ID", "Data", "Cliente", "Origem", "Método", "Valor", "Situação"],
      ...(data?.data || []).map((p) => [
        p.paymentId,
        formatDate(p.paidAt),
        p.companyName,
        PROVEDOR_META[p.provider]?.label || p.provider,
        p.method,
        String(p.amount).replace(".", ","),
        p.status,
      ]),
    ];

    const csv = linhas
      .map((linha) =>
        linha.map((celula) => `"${String(celula ?? "").replace(/"/g, '""')}"`).join(";")
      )
      .join("\n");

    const url = URL.createObjectURL(
      new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8" })
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `pagamentos-pagina-${pagina}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Pagamentos</h1>
          <p className="text-sm text-slate-500">
            Tudo o que a Relinq recebeu — gateways e lançamentos feitos à mão.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setCriando(true)}
            className="inline-flex items-center gap-1.5 rounded-xl bg-pink-600 px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-pink-700"
          >
            <MdAdd size={18} /> Criar pagamento
          </button>

        {data?.data?.length ? (
          <button
            type="button"
            onClick={exportarPagina}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-pink-200"
          >
            <MdOutlineFileDownload size={16} /> Exportar esta página
          </button>
        ) : null}
        </div>
      </header>

      <Card className="overflow-hidden">
        <div className="flex flex-col divide-y divide-slate-100 sm:flex-row sm:divide-y-0">
          <Kpi
            icon={MdOutlinePayments}
            label="Recebido"
            value={formatMoney(resumo?.totalAmount || 0)}
            hint={`${formatNumber(resumo?.approvedCount || 0)} pagamento(s) confirmado(s)`}
            tone="success"
          />
          <Kpi
            icon={MdOutlineReceiptLong}
            label="Ticket médio"
            value={formatMoney(ticket)}
            hint="média por pagamento"
          />
          <Kpi
            icon={MdOutlineAccountBalanceWallet}
            label="Pelos gateways"
            value={formatMoney(
              (resumo?.asaasAmount || 0) + (resumo?.mercadoPagoAmount || 0)
            )}
            hint={`Asaas ${formatNumber(resumo?.asaasCount || 0)} · MP ${formatNumber(
              resumo?.mercadoPagoCount || 0
            )}`}
            tone="info"
          />
          <Kpi
            icon={MdOutlineEditNote}
            label="Lançados à mão"
            value={formatMoney(resumo?.manualAmount || 0)}
            hint={`${formatNumber(resumo?.manualCount || 0)} registro(s) sem gateway`}
            tone={resumo?.manualCount ? "warning" : "neutral"}
          />
        </div>
      </Card>

      {data?.statusBreakdown?.length ? (
        <Card className="p-4">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            Situação dos pagamentos
          </p>

          {/*
            Barra proporcional em vez de quatro contadores: o que importa aqui
            é a PROPORÇÃO de recusados e pendentes sobre o total — um número
            solto de "12 recusados" não diz se é muito ou pouco.
          */}
          <div className="flex h-7 w-full gap-[2px] overflow-hidden rounded-xl">
            {data.statusBreakdown.map((item) => (
              <span
                key={item.key}
                title={`${item.label}: ${formatNumber(item.count)} (${formatPercent(item.percent)})`}
                style={{ width: `${Math.max(item.percent, 2)}%` }}
                className={`flex items-center justify-center ${
                  item.tone === "success"
                    ? "bg-emerald-500"
                    : item.tone === "warning"
                    ? "bg-amber-500"
                    : item.tone === "danger"
                    ? "bg-rose-500"
                    : "bg-slate-400"
                }`}
              >
                <span className="px-1 text-[10px] font-bold text-white/95">
                  {item.percent >= 8 ? formatPercent(item.percent) : ""}
                </span>
              </span>
            ))}
          </div>

          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            {data.statusBreakdown.map((item) => (
              <span
                key={item.key}
                className="flex items-center gap-1.5 text-[11px] text-slate-600"
              >
                <span
                  className={`h-2 w-2 rounded-full ${
                    item.tone === "success"
                      ? "bg-emerald-500"
                      : item.tone === "warning"
                      ? "bg-amber-500"
                      : item.tone === "danger"
                      ? "bg-rose-500"
                      : "bg-slate-400"
                  }`}
                />
                {item.label}
                <span className="font-semibold text-slate-800">
                  {formatNumber(item.count)}
                </span>
                <span className="text-slate-400">
                  {formatPercent(item.percent)}
                </span>
                <span className="text-slate-400">· {formatMoney(item.amount)}</span>
              </span>
            ))}
          </div>
        </Card>
      ) : null}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Card className="overflow-hidden xl:col-span-2">
          <div className="border-b border-slate-100 px-4 py-3">
            <h2 className="text-sm font-bold text-slate-900">Recebido por mês</h2>
            <p className="text-[11px] text-slate-500">
              Sobre o recorte filtrado inteiro, não só a página
            </p>
          </div>
          {isLoading ? (
            <SkeletonChart height={200} bars={12} withHeader={false} />
          ) : data?.series?.length ? (
            <div className="p-3">
              <ReceitaChart series={data.series} />
            </div>
          ) : (
            <p className="px-4 py-10 text-center text-xs text-slate-400">
              Sem pagamentos confirmados no recorte.
            </p>
          )}
        </Card>

        <Card className="p-4">
          <h2 className="mb-3 text-sm font-bold text-slate-900">Filtrar</h2>

          <div className="space-y-2">
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                Período
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  className={campoClass}
                  value={filtros.startDate}
                  onChange={(e) =>
                    setFiltros((p) => ({ ...p, startDate: e.target.value }))
                  }
                />
                <input
                  type="date"
                  className={campoClass}
                  value={filtros.endDate}
                  onChange={(e) =>
                    setFiltros((p) => ({ ...p, endDate: e.target.value }))
                  }
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                Origem
              </label>
              <select
                className={campoClass}
                value={filtros.provider}
                onChange={(e) =>
                  setFiltros((p) => ({ ...p, provider: e.target.value }))
                }
              >
                {PROVEDORES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={aplicar}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-slate-900 py-2 text-xs font-semibold text-white transition hover:bg-slate-700"
              >
                <MdOutlineSearch size={15} /> Aplicar
              </button>
              <button
                type="button"
                onClick={limpar}
                className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:border-pink-200"
              >
                Limpar
              </button>
            </div>
          </div>
        </Card>
      </div>

      <Card className="overflow-hidden">
        {isLoading ? (
          <SkeletonTable rows={8} cols={6} withAvatar={false} />
        ) : error ? (
          <ErrorBlock
            message="Não foi possível carregar os pagamentos."
            onRetry={refetch}
          />
        ) : !data.data.length ? (
          <EmptyState
            icon={MdOutlineReceiptLong}
            title="Nenhum pagamento no recorte"
            description="Limpe o período ou troque a origem. Pagamentos lançados à mão aparecem como 'Manual'."
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[780px] text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3">Cliente</th>
                    <th className="px-4 py-3">Data</th>
                    <th className="px-4 py-3">Origem</th>
                    <th className="px-4 py-3">Método</th>
                    <th className="px-4 py-3 text-right">Valor</th>
                    <th className="px-4 py-3">Situação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.data.map((pagamento) => {
                    const meta =
                      PROVEDOR_META[pagamento.provider] || PROVEDOR_META.unknown;

                    return (
                      <tr key={pagamento.id} className="hover:bg-slate-50/60">
                        <td className="px-4 py-2.5">
                          <Link
                            to={`/clientes/${pagamento.companyId}`}
                            className="block truncate font-semibold text-slate-900 hover:text-pink-700"
                          >
                            {pagamento.companyName}
                          </Link>
                          <span className="text-[11px] text-slate-400">
                            #{pagamento.companyId}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-slate-600">
                          {formatDate(pagamento.paidAt)}
                        </td>
                        <td className="px-4 py-2.5">
                          <Badge tone={meta.tone}>{meta.label}</Badge>
                        </td>
                        <td className="px-4 py-2.5 text-slate-600">
                          {pagamento.method}
                        </td>
                        <td className="px-4 py-2.5 text-right font-semibold text-slate-900">
                          {formatMoney(pagamento.amount)}
                        </td>
                        <td className="px-4 py-2.5">
                          {/*
                            A tradução e a cor vêm do servidor
                            (paymentService.classifyStatus): cada gateway
                            nomeia o mesmo desfecho de um jeito, e deixar a
                            tela adivinhar faria "approved" aparecer em inglês
                            e um provedor novo quebrar a cor.
                          */}
                          <Badge tone={pagamento.statusGroup?.tone || "neutral"}>
                            {pagamento.statusGroup?.label || pagamento.status}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 px-4 py-3">
              <span className="text-xs text-slate-500">
                {formatNumber(data.totalCount)} pagamento(s) · página{" "}
                {data.page} de {data.totalPages}
                {isFetching ? " · atualizando…" : ""}
              </span>

              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={data.page <= 1}
                  onClick={() => setPagina((p) => p - 1)}
                  className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 disabled:opacity-40"
                >
                  anterior
                </button>
                <button
                  type="button"
                  disabled={data.page >= data.totalPages}
                  onClick={() => setPagina((p) => p + 1)}
                  className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 disabled:opacity-40"
                >
                  próxima
                </button>
              </div>
            </div>
          </>
        )}
      </Card>

      {/*
        Lançamento manual a partir desta tela. O mesmo que existe na ficha do
        cliente — a diferença é que aqui o cliente ainda precisa ser
        escolhido, e é o caso comum de quem está conferindo os recebimentos do
        dia e encontra um PIX que não passou por gateway.
      */}
      <Modal
        isOpen={criando}
        onClose={setCriando}
        size="md"
        icon={MdOutlineBusinessCenter}
        title="Registrar pagamento"
        subtitle="Para o que a cliente pagou fora do sistema — PIX, dinheiro, transferência"
      >
        <div className="space-y-3">
          {erroForm ? (
            <p className="rounded-xl bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-800 ring-1 ring-inset ring-rose-200">
              {erroForm}
            </p>
          ) : null}

          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
              Cliente
            </label>
            <ClientPicker value={cliente} onChange={setCliente} />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                Valor recebido
              </label>
              <input
                value={form.value}
                onChange={(e) => setForm((p) => ({ ...p, value: e.target.value }))}
                placeholder="149,70"
                inputMode="decimal"
                className={campoClass}
              />
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                Data do pagamento
              </label>
              <input
                type="date"
                max={hojeIso()}
                value={form.paidAt}
                onChange={(e) => setForm((p) => ({ ...p, paidAt: e.target.value }))}
                className={campoClass}
              />
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                Forma
              </label>
              <select
                value={form.billingType}
                onChange={(e) =>
                  setForm((p) => ({ ...p, billingType: e.target.value }))
                }
                className={campoClass}
              >
                {FORMAS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                Acesso
              </label>
              <select
                value={form.extend}
                onChange={(e) => setForm((p) => ({ ...p, extend: e.target.value }))}
                className={campoClass}
              >
                {EXTENSOES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
              Observação
            </label>
            <input
              value={form.note}
              onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
              placeholder="Ex.: pagou 3 meses adiantado, combinado no WhatsApp"
              className={campoClass}
            />
          </div>

          <button
            type="button"
            onClick={() => registrar.mutate()}
            disabled={!cliente || !form.value.trim() || registrar.isLoading}
            className="w-full rounded-xl bg-pink-600 py-2.5 text-sm font-semibold text-white transition hover:bg-pink-700 disabled:opacity-40"
          >
            {registrar.isLoading
              ? "Registrando..."
              : cliente
              ? `Registrar para ${cliente.name}`
              : "Escolha um cliente"}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default PaymentsTable;
